import { atom } from 'jotai';
import { RESET } from 'jotai/utils';
import { v4 as uuid } from 'uuid';
import { BarkerEventListing, InventorySeasonLocationExtended, SeasonSummary } from '../types';
import { localRuleDefaultAtom, overrideSelectedMergedListingAtom, ruleStateAtom, selectedMarketplaceEventAtom, selectedSectionAtom } from './atoms';
import { extendRuleFromSettings } from '../utils/rule-utils';
import { auth } from './atoms.auth';
import { focusAtom } from 'jotai-optics';
import { BrokerNerdsBarkerCoreModelsInventorySeasonLocationListing } from '../api';
import { TenantIdEventId } from '../models/tenantIdListingId';

export namespace seasons {
  export const selectedSeasonAtom = atom<SeasonSummary | undefined>(undefined);
  export const selectedSeasonLocationsAtom = focusAtom(selectedSeasonAtom, (o) => o.valueOr({} as { locations: [] | undefined }).prop('locations'));
  export const updateLocationAtom = atom(null, (get, set, hashId: string, update: Partial<InventorySeasonLocationExtended>) => {
    const season = get(selectedSeasonAtom);
    const locations = [...(season?.locations ?? [])];
    const locationIndex = locations.findIndex((l) => l.hashId === hashId);

    if (season && locationIndex !== -1) {
      locations[locationIndex] = { ...locations[locationIndex], ...update };

      set(selectedSeasonAtom, {
        ...season,
        locations,
      });

      const location = locations[locationIndex];
      if (get(_selectedLocationAtom)?.hashId === location.hashId) {
        set(_selectedLocationAtom, location);
      }
    }
  });
  export const updateLocationListingAtom = atom(null, (get, set, listingId: string, update: Partial<BrokerNerdsBarkerCoreModelsInventorySeasonLocationListing>) => {
    get(selectedSeasonLocationsAtom)?.forEach((location) => {
      if (location.listings.some((x) => x.listingId === listingId)) {
        const listings = location.listings.map((l) => (l.listingId === listingId ? { ...l, ...update } : l));
        set(updateLocationAtom, location.hashId, { listings });
      }
    });
  });

  const _initialSelectedLocationAtom = atom<InventorySeasonLocationExtended | undefined>(undefined);
  const _selectedLocationAtom = atom<InventorySeasonLocationExtended | undefined>(undefined);
  export const previousSelectedLocationAtom = atom<InventorySeasonLocationExtended | undefined>(undefined);
  export const selectedLocationAtom = atom(
    (get) => get(_selectedLocationAtom),
    (get, set, update: ReturnType<typeof _selectedLocationAtom.read> | typeof RESET) => {
      if (update === RESET) {
        const initialValue = get(_initialSelectedLocationAtom);
        set(_selectedLocationAtom, initialValue);
      } else {
        const last = get(_selectedLocationAtom);
        if (last?.hashId !== update?.hashId) {
          set(_initialSelectedLocationAtom, update);
          if (update !== undefined) {
            set(setSeasonRuleFromLocationAtom, update);
          }
        }
        set(previousSelectedLocationAtom, last);
        set(_selectedLocationAtom, update);
        set(selectedSectionAtom, update?.section);
        // @ts-ignore - HACK: Satisfies is used to give fake bare minimum object to satisfy ruleStateAtom
        set(overrideSelectedMergedListingAtom, {
          tenantIdEventId: TenantIdEventId.create(update?.tenantId!, update?.listings[0]?.eventId!).toStringTyped(),
          tenantIdListingId: TenantIdEventId.create(update?.tenantId!, update?.listings[0]?.listingId!).toStringTyped(),
          tenantId: update?.tenantId!,
          listingId: update?.listings[0]?.listingId!,
          row: update?.row!,
          quantityRemaining: update?.listings[0]?.quantity!,
          event: {
            localDateTime: new Date(),
            eventId: '',
            links: [],
            monitors: [],
            name: '',
            numComments: 0,
            numTicketsSold: 0,
            openCost: 0,
            openListings: 0,
            openTickets: 0,
            performer: {
              category: {
                categoryId: '',
                name: '',
                type: '',
              },
              name: '',
              performerId: '',
            },
            statusId: 'Unknown',
            tenantId: '',
            venue: {
              city: undefined,
              countryCode: undefined,
              enrichmentData: undefined,
              lastEnrichedAt: undefined,
              latLong: undefined,
              name: '',
              postalCode: undefined,
              state: undefined,
              tenantId: '',
              timeZone: undefined,
              venueId: '',
            },
          },
        } satisfies Partial<BarkerEventListing>);
      }
    },
  );

  export const setSeasonRuleFromLocationAtom = atom(null, (get, set, update: InventorySeasonLocationExtended) => {
    const marketplaceId = get(selectedMarketplaceEventAtom)?.marketplaceId;
    const defaultRule = get(localRuleDefaultAtom);
    const principalPricerSettings = get(auth.principalAtom)?.settings?.pricerSettings;
    const availableMarketplaces = get(auth.tenantsAtom).find((t) => t.tenantId === update?.tenantId)?.marketplaceIds;
    const tenantPricerSettings = get(auth.tenantsAtom).filter((t) => t.tenantId === update?.tenantId)[0]?.settings?.pricerSettings;
    const newRule = extendRuleFromSettings(
      defaultRule,
      marketplaceId,
      update?.row,
      update?.listings[0].quantity ?? 0,
      principalPricerSettings,
      tenantPricerSettings,
      availableMarketplaces,
      {
        isAutoPriced: false,
      },
    );
    newRule.ruleId = uuid();
    set(ruleStateAtom, newRule);
  });

  export const clearRuleStateFromLocationsAtom = atom(null, (get, set) => {
    const season = get(seasons.selectedSeasonAtom);
    season?.locations.forEach((location) => {
      set(seasons.updateLocationAtom, location.hashId, { ruleId: undefined, ruleTier: undefined, ruleCount: undefined });
    });
  });
}
